/**
 * Review — the week, and what moved it.
 *
 * This was the bottom half of Overview, where it was read by nobody: a 30-day
 * chart underneath the thing you actually opened the app for. Weekly reading is
 * a different activity from logging — it happens sitting down, once, not in a
 * kitchen four times a day — and burying it under the ring meant scrolling past
 * today to reach a judgement about the week.
 *
 * Nothing here draws before it can say something true. Four logged days is the
 * threshold for an average and for the weight-against-intake read; below that
 * the numbers exist but they are noise wearing the costume of a finding, and
 * showing them teaches people to distrust the ones that come later.
 */

import { useIntakeCorrelation, useOverview, useWeightTrend } from "@calorio/core";
import { Link } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { IntakeChart } from "../components/IntakeChart";

/**
 * Days of data before an average is worth printing.
 *
 * Stated once, here, because the same number governs three different panels and
 * the honest thing is for them to appear together. A week's worth of anything
 * derived from two days is arithmetic, not evidence.
 */
const ENOUGH_DAYS = 4;

export function Review() {
  const ctx = useCalorio();
  const overview = useOverview(ctx, 30);
  const weight = useWeightTrend(ctx, 90);
  const correlation = useIntakeCorrelation(ctx);

  if (overview.isLoading) return <p className="muted">Loading…</p>;
  if (!overview.data) return <p className="error">Couldn't load your week.</p>;

  const { intake, streak } = overview.data;
  const logged = intake.logged_days;
  const enough = logged >= ENOUGH_DAYS;

  return (
    <div className="stack">
      <header>
        <h1 style={{ fontSize: "var(--step-2)", margin: 0 }}>Review</h1>
        <p className="stat__label" style={{ margin: 0 }}>
          <span className="num">{logged}</span> logged {logged === 1 ? "day" : "days"}
          {streak.current > 0 && (
            <>
              {" · "}
              <span aria-hidden="true">🔥</span> <span className="num">{streak.current}</span>-day
              streak
            </>
          )}
        </p>
      </header>

      {!enough ? (
        /* Declining to draw, and saying what unlocks it. An empty panel reads as
           a broken feature; a panel that explains itself reads as a threshold,
           which is what it is. */
        <div className="card stack" style={{ gap: "var(--sp-2)" }}>
          <h2 className="card__title">Not enough days yet</h2>
          <p className="manual__note">
            {logged === 0
              ? "Nothing logged this week."
              : `${logged} ${logged === 1 ? "day" : "days"} in.`}{" "}
            Averages and the weight-against-intake read appear once there are{" "}
            <span className="num">{ENOUGH_DAYS}</span> days to average — before that they&rsquo;d be
            noise dressed as a finding.
          </p>
          <p className="manual__note">
            <Link to="/" className="muted">
              Log something today →
            </Link>
          </p>
        </div>
      ) : (
        <>
          <div className="review__stats">
            <div className="review__stat">
              <span className="stat__label">Daily average</span>
              <span className="stat__value num">
                {Math.round(intake.average_kcal ?? 0).toLocaleString()}
              </span>
              {intake.average_vs_target != null && (
                <span
                  className={`stat__label num ${
                    intake.average_vs_target <= 0 ? "delta--down" : "delta--up"
                  }`}
                >
                  {intake.average_vs_target > 0 ? "+" : ""}
                  {Math.round(intake.average_vs_target)} vs target
                </span>
              )}
            </div>

            <div className="review__stat">
              <span className="stat__label">Days logged</span>
              <span className="stat__value num">{logged}</span>
              <span className="stat__label num">of {intake.window_days}</span>
            </div>

            <div className="review__stat">
              <span className="stat__label">Weight trend</span>
              <span className="stat__value num">
                {weight.data?.entries.length
                  ? weight.data.entries.at(-1)?.weight_kg.toFixed(1)
                  : "—"}
              </span>
              {weight.data?.change_kg != null && (
                <span
                  className={`stat__label num ${
                    weight.data.change_kg <= 0 ? "delta--down" : "delta--up"
                  }`}
                >
                  {weight.data.change_kg > 0 ? "+" : ""}
                  {weight.data.change_kg.toFixed(1)} kg
                </span>
              )}
            </div>
          </div>

          <div className="card">
            <h2 className="card__title">Intake by day</h2>
            <IntakeChart intake={intake} />
          </div>

          {/* The one genuine finding on this page, and the one most able to
              mislead — so it holds itself to a higher bar than the panels above
              and stays silent below four weeks of comparison, not four days. */}
          {correlation.data &&
            correlation.data.weeks_under + correlation.data.weeks_over >= ENOUGH_DAYS && (
              <div className="card stack" style={{ gap: "var(--sp-2)" }}>
                <h2 className="card__title">Weight against intake</h2>
                <p style={{ margin: 0 }}>
                  Weeks under{" "}
                  <span className="num">{correlation.data.split_kcal.toLocaleString()}</span> kcal
                  averaged{" "}
                  <span className="num">
                    {correlation.data.mean_change_under_kg?.toFixed(1) ?? "—"} kg
                  </span>
                  ; weeks over averaged{" "}
                  <span className="num">
                    {correlation.data.mean_change_over_kg?.toFixed(1) ?? "—"} kg
                  </span>
                  .
                </p>
                <p className="stat__label" style={{ margin: 0 }}>
                  <span className="num">{correlation.data.weeks_under}</span> weeks under,{" "}
                  <span className="num">{correlation.data.weeks_over}</span> over
                </p>
              </div>
            )}
        </>
      )}
    </div>
  );
}
